import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const HospitalAgentModal = ({ isOpen, onClose, onSave, agentToEdit }) => {
  const isEditMode = !!agentToEdit;
  const [agentData, setAgentData] = useState({
    name: isEditMode && agentToEdit?.name ? agentToEdit.name : "",
    contactNo: isEditMode && agentToEdit?.contactNo ? agentToEdit.contactNo : "",
    address: isEditMode && agentToEdit?.address ? agentToEdit.address : "",
    division: isEditMode && agentToEdit?.division ? agentToEdit.division : "Dhaka",
    district: isEditMode && agentToEdit?.district ? agentToEdit.district : "Dhaka",
    upazila: isEditMode && agentToEdit?.upazila ? agentToEdit.upazila : "",
    union: isEditMode && agentToEdit?.union ? agentToEdit.union : "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setAgentData({
        name: isEditMode && agentToEdit?.name ? agentToEdit.name : "",
        contactNo: isEditMode && agentToEdit?.contactNo ? agentToEdit.contactNo : "",
        address: isEditMode && agentToEdit?.address ? agentToEdit.address : "",
        division: isEditMode && agentToEdit?.division ? agentToEdit.division : "Dhaka",
        district: isEditMode && agentToEdit?.district ? agentToEdit.district : "Dhaka",
        upazila: isEditMode && agentToEdit?.upazila ? agentToEdit.upazila : "",
        union: isEditMode && agentToEdit?.union ? agentToEdit.union : "",
      });
      setErrors({});
    }
  }, [isOpen, agentToEdit]);

  const validateForm = () => {
    const newErrors = {};
    let hasError = false;

    // Strict validation for add mode (POST requires agentName and contactNumber)
    if (!isEditMode) {
      if (!agentData.name.trim()) {
        newErrors.name = "Agent Name is required";
        hasError = true;
      }
      if (!agentData.contactNo.trim()) {
        newErrors.contactNo = "Contact Number is required";
        hasError = true;
      }
    }

    // In edit mode, ensure at least one field is provided (PUT requirement)
    if (isEditMode) {
      const hasChanges =
        agentData.name.trim() ||
        agentData.contactNo.trim() ||
        agentData.address.trim() ||
        agentData.division.trim() ||
        agentData.district.trim() ||
        agentData.upazila.trim() ||
        agentData.union.trim();
      if (!hasChanges) {
        newErrors.form = "At least one field must be provided to update";
        hasError = true;
      }
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error(newErrors.form || "Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgentData((prevData) => ({ ...prevData, [name]: value }));
    if (value.trim() || !isEditMode) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.form;
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let savedAgent;
      if (isEditMode) {
        // For edit mode, call PUT API with only provided fields
        const payload = {};
        if (agentData.name.trim()) payload.agentName = agentData.name.trim();
        if (agentData.contactNo.trim()) payload.contactNumber = agentData.contactNo.trim();
        if (agentData.address.trim()) payload.address = agentData.address.trim();
        if (agentData.division.trim()) payload.division = agentData.division.trim();
        if (agentData.district.trim()) payload.district = agentData.district.trim();
        if (agentData.upazila.trim()) payload.upazila = agentData.upazila.trim();
        if (agentData.union.trim()) payload.union = agentData.union.trim();

        // console.log("PUT request payload:", payload);
        const response = await axios.put(
          `https://api.muktihospital.com/api/hospital/agent/edit/${agentToEdit.id}`,
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        // console.log("PUT response:", response.data);
        savedAgent = {
          id: agentToEdit.id,
          agentCode: response.data.agent.agentCode || agentToEdit.agentCode || "",
          name: response.data.agent.agentName || agentData.name.trim() || "",
          contactNo: response.data.agent.contactNumber || agentData.contactNo.trim() || "",
          address: response.data.agent.address || agentData.address.trim() || "",
          division: response.data.agent.division || agentData.division.trim() || "Dhaka",
          district: response.data.agent.district || agentData.district.trim() || "Dhaka",
          upazila: response.data.agent.upazila || agentData.upazila.trim() || "",
          union: response.data.agent.union || agentData.union.trim() || "",
        };
        // console.log("Saved agent:", savedAgent);
      } else {
        // For add mode, call POST API
        const payload = {
          agentName: agentData.name.trim(),
          contactNumber: agentData.contactNo.trim(),
          address: agentData.address.trim() || "",
          division: agentData.division.trim() || "Dhaka",
          district: agentData.district.trim() || "Dhaka",
          upazila: agentData.upazila.trim() || "",
          union: agentData.union.trim() || "",
        };
        // console.log("POST request payload:", payload);
        const response = await axios.post(
          "https://api.muktihospital.com/api/hospital/agent/add",
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        // console.log("POST response:", response.data);
        savedAgent = {
          id: response.data.agent.id || Math.random().toString(36).substr(2, 9),
          agentCode: response.data.agent.agentCode || "",
          name: response.data.agent.agentName || agentData.name.trim(),
          contactNo: response.data.agent.contactNumber || agentData.contactNo.trim(),
          address: response.data.agent.address || agentData.address.trim() || "",
          division: response.data.agent.division || agentData.division.trim() || "Dhaka",
          district: response.data.agent.district || agentData.district.trim() || "Dhaka",
          upazila: response.data.agent.upazila || agentData.upazila.trim() || "",
          union: response.data.agent.union || agentData.union.trim() || "",
        };
        // console.log("Saved agent:", savedAgent);
      }
      onSave(savedAgent, isEditMode);
      onClose();
    } catch (error) {
      console.error("Error saving agent:", error);
      if (error.response?.status === 409) {
        toast.error("An agent with this name already exists. Please choose a unique name.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.error || "Missing or invalid agent fields.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (error.response?.status === 404) {
        toast.error("Agent not found.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to save agent.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[100vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">{isEditMode ? "Edit Agent" : "Add Agent"}</h2>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agent Name <span className="text-red-500">{!isEditMode ? "*" : ""}</span>
            </label>
            <input
              type="text"
              name="name"
              value={agentData.name}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.name ? "border-red-500" : ""
              }`}
              placeholder="Enter agent name"
              disabled={isLoading}
            />
            {errors.name && (
              <span className="text-red-500 text-xs mt-1">{errors.name}</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number <span className="text-red-500">{!isEditMode ? "*" : ""}</span>
            </label>
            <input
              type="text"
              name="contactNo"
              value={agentData.contactNo}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.contactNo ? "border-red-500" : ""
              }`}
              placeholder="Enter contact number"
              disabled={isLoading}
            />
            {errors.contactNo && (
              <span className="text-red-500 text-xs mt-1">{errors.contactNo}</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={agentData.address}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter address"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Division
            </label>
            <input
              type="text"
              name="division"
              value={agentData.division}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter division"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              District
            </label>
            <input
              type="text"
              name="district"
              value={agentData.district}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter district"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upazila
            </label>
            <input
              type="text"
              name="upazila"
              value={agentData.upazila}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter upazila"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Union
            </label>
            <input
              type="text"
              name="union"
              value={agentData.union}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter union"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalAgentModal;